import { useState } from 'react';
import { formatServerDateTime, useDisplayZone, useSession } from '@adiox/ui';
import { useSymbols } from '../market/useSymbols';
import { useStream } from '../stream/stream';
import { useQuoteSubscription } from '../stream/useQuoteSubscription';
import { SymbolPicker } from '../trading/SymbolPicker';
import { useWorkspace } from '../workspace/workspaceStore';
import { CONDITIONS, conditionLabel, crossed, newAlertProblem, type PriceAlert } from './alertModel';
import { useAlerts } from './useAlerts';

const field =
  'w-full rounded-md border border-line bg-panel2 px-2 py-1 text-sm outline-none focus:border-accent';
const label = 'mb-1 block text-xs uppercase tracking-wide text-muted';
const small =
  'rounded border border-line px-2 py-0.5 text-xs hover:border-accent disabled:cursor-not-allowed disabled:opacity-40';
const th = 'px-2 py-1 text-left text-xs font-normal uppercase tracking-wide text-muted';
const td = 'px-2 py-1 whitespace-nowrap';

/**
 * Price alerts, like MT5's Alerts tab: each goes off once when the bid or ask reaches its price, with a pop-up and an entry
 * in the notification log. The server keeps and watches them, so they go off even with no terminal open; the next time the
 * terminal opens it lists the ones that went off meanwhile.
 */
export function AlertsPanel() {
  const { alerts, add: addAlert, setActive, remove } = useAlerts();
  const readOnly = useSession().user?.readOnly === true;
  const [busy, setBusy] = useState(false);
  const active = useWorkspace((s) => s.activeSymbol);
  const symbols = useSymbols();
  const zone = useDisplayZone();
  const [symbol, setSymbol] = useState<string | null>(null);
  const chosen = symbol ?? active ?? symbols.data?.[0]?.name ?? '';
  const [condition, setCondition] = useState<(typeof CONDITIONS)[number]['value']>('bid-above');
  const [price, setPrice] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const quote = useStream((s) => (chosen ? s.quotes[chosen] : undefined));
  const quotes = useStream((s) => s.quotes);
  useQuoteSubscription(chosen ? [chosen] : []);
  const digits = symbols.data?.find((s) => s.name === chosen)?.digits ?? 5;
  const digitsOf = (name: string) => symbols.data?.find((s) => s.name === name)?.digits ?? 5;

  const add = () => {
    const [f, direction] = condition.split('-') as [PriceAlert['field'], PriceAlert['direction']];
    const draft = { symbol: chosen, field: f, direction, price: Number(price) };
    const issue = newAlertProblem(draft, quote, alerts.length);
    if (issue) return setProblem(issue);
    void act(
      () => addAlert(draft),
      () => setPrice(''),
    );
  };

  /** Runs one server change, showing the server's reason when it refuses. */
  const act = async (change: () => ReturnType<typeof addAlert>, done?: () => void) => {
    setBusy(true);
    setProblem(null);
    const outcome = await change();
    setBusy(false);
    if (outcome.ok) done?.();
    else setProblem(outcome.message);
  };

  return (
    <section className="flex h-full flex-col bg-panel" aria-label="Alerts">
      <form
        className="flex flex-wrap items-end gap-2 border-b border-line p-2"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          add();
        }}
      >
        <div className="w-32">
          <label className={label} htmlFor="alert-symbol">
            Symbol
          </label>
          <SymbolPicker
            id="alert-symbol"
            className={field}
            value={chosen}
            options={symbols.data ?? []}
            onSelect={setSymbol}
          />
        </div>
        <div className="w-36">
          <label className={label} htmlFor="alert-condition">
            When
          </label>
          <select
            id="alert-condition"
            className={field}
            value={condition}
            onChange={(e) => setCondition(e.target.value as typeof condition)}
          >
            {CONDITIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div className="w-32">
          <label className={label} htmlFor="alert-price">
            Price
          </label>
          <input
            id="alert-price"
            type="number"
            step={String(1 / 10 ** digits)}
            className={`${field} num`}
            placeholder={quote ? quote[condition.startsWith('bid') ? 'bid' : 'ask'].toFixed(digits) : ''}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
        </div>
        <button
          type="submit"
          disabled={busy || readOnly}
          className="rounded-md bg-accent px-3 py-1 text-sm font-semibold text-accent-fg disabled:opacity-40"
        >
          Add alert
        </button>
        <span className="num text-xs text-muted">
          {quote ? `Now ${quote.bid.toFixed(digits)} / ${quote.ask.toFixed(digits)}` : ''}
        </span>
        <p role="alert" className="w-full text-sm text-down empty:hidden">
          {problem}
        </p>
      </form>
      <div className="min-h-0 flex-1 overflow-auto" tabIndex={0} role="group" aria-label="Alert list">
        {alerts.length === 0 ? (
          <p className="p-4 text-sm text-muted">
            No alerts. Add one above: it goes off once when the price gets there, even with the terminal
            closed, and you are told the next time you open it.
          </p>
        ) : (
          <table className="w-full text-sm" aria-label="Price alerts">
            <thead className="sticky top-0 bg-panel">
              <tr className="border-b border-line">
                <th className={th} scope="col">
                  Symbol
                </th>
                <th className={th} scope="col">
                  Condition
                </th>
                <th className={`${th} text-right`} scope="col">
                  Price
                </th>
                <th className={th} scope="col">
                  Status
                </th>
                <th className={th} scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((a) => (
                <tr key={a.id} className="border-b border-line/40" data-testid="alert-row">
                  <td className={`${td} font-semibold`}>{a.symbol}</td>
                  <td className={td}>{conditionLabel(a)}</td>
                  <td className={`${td} num text-right`}>{a.price.toFixed(digitsOf(a.symbol))}</td>
                  <td className={`${td} ${a.active ? 'text-up' : 'text-muted'}`} data-testid="alert-status">
                    {a.active
                      ? 'Waiting'
                      : a.triggeredAt
                        ? `Went off ${formatServerDateTime(a.triggeredAt, zone.offsetAt)}${
                            a.triggeredValue === undefined
                              ? ''
                              : ` at ${a.triggeredValue.toFixed(digitsOf(a.symbol))}`
                          }`
                        : 'Off'}
                  </td>
                  <td className={`${td} text-right`}>
                    <button
                      type="button"
                      className={small}
                      disabled={busy || readOnly}
                      onClick={() => {
                        if (!a.active && crossed({ ...a, active: true }, quotes[a.symbol])) {
                          return setProblem(
                            `${a.symbol}: the ${a.field} is already past ${a.price}, so it would go off at once. Delete it or add a new one.`,
                          );
                        }
                        void act(() => setActive(a.id, !a.active));
                      }}
                    >
                      {a.active ? 'Turn off' : 'Turn on'}
                    </button>{' '}
                    <button
                      type="button"
                      className={small}
                      disabled={busy || readOnly}
                      aria-label={`Delete alert ${a.symbol} ${conditionLabel(a).toLowerCase()} ${a.price}`}
                      onClick={() => void act(() => remove(a.id))}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
